import apiClient from '@/utils/apiClient';
import {
    getManufacturerCodes,
    MANUFACTURER_CODES,
    setManufacturerCodes,
    upsertManufacturer,
} from '@/data/manufacturerCodes';
import {
    getFurnizori,
    setFurnizori,
    upsertFurnizor,
} from '@/data/furnizorNames';
import {
    PRODUCT_STRUCTURE_BY_SYSTEM,
    setProductStructureBySystem,
} from '@/data/productStructureLookups';

let loadPromise = null;
let lookupsResolved = false;

/**
 * Loads manufacturer codes, furnizori, and product structure lookups from the API.
 * Falls back to bundled static data when the API is unavailable.
 * @returns {Promise<void>}
 */
export async function ensureProductLookupsLoaded() {
    if (lookupsResolved) {
        return;
    }
    if (loadPromise) {
        return loadPromise;
    }

    loadPromise = Promise.all([
        apiClient.get('/api/product-manufacturers/'),
        apiClient.get('/api/product-structure/'),
        apiClient.get('/api/furnizori/').catch(() => ({ data: [] })),
    ])
        .then(([manufacturersResponse, structureResponse, furnizoriResponse]) => {
            const manufacturers = manufacturersResponse.data;
            const structure = structureResponse.data?.structure;
            const furnizori = furnizoriResponse?.data;
            if (Array.isArray(manufacturers) && manufacturers.length) {
                setManufacturerCodes(manufacturers);
            }
            if (structure && Object.keys(structure).length) {
                setProductStructureBySystem(structure);
            }
            if (Array.isArray(furnizori)) {
                setFurnizori(furnizori);
            }
        })
        .catch((error) => {
            console.error('Failed to load product lookups from API, using static fallbacks.', error);
            setManufacturerCodes(MANUFACTURER_CODES);
            setProductStructureBySystem(PRODUCT_STRUCTURE_BY_SYSTEM);
            setFurnizori([]);
        })
        .finally(() => {
            lookupsResolved = true;
            loadPromise = null;
        });

    return loadPromise;
}

/**
 * Creates a manufacturer in the catalog and updates the local cache.
 * @param {{ code: string, name: string }} payload
 * @returns {Promise<{ id?: number|null, code: string, name: string }>}
 */
export async function createManufacturer(payload) {
    const code = String(payload?.code ?? '').trim();
    const name = String(payload?.name ?? '').trim();
    if (!code || !name) {
        throw new Error('Codul și denumirea producătorului sunt obligatorii.');
    }
    const response = await apiClient.post('/api/product-manufacturers/', { code, name });
    const created = upsertManufacturer(response.data);
    if (!created) {
        throw new Error('Răspuns invalid la crearea producătorului.');
    }
    return created;
}

/**
 * Updates a manufacturer in the catalog and updates the local cache.
 * @param {{ id: number, code: string, name: string }} payload
 * @returns {Promise<{ id?: number|null, code: string, name: string }>}
 */
export async function updateManufacturer(payload) {
    const id = payload?.id;
    const code = String(payload?.code ?? '').trim();
    const name = String(payload?.name ?? '').trim();
    if (id == null) {
        throw new Error('ID-ul producătorului este obligatoriu.');
    }
    if (!code || !name) {
        throw new Error('Codul și denumirea producătorului sunt obligatorii.');
    }
    const response = await apiClient.patch(`/api/product-manufacturers/${id}/`, { code, name });
    const updated = upsertManufacturer(response.data);
    if (!updated) {
        throw new Error('Răspuns invalid la actualizarea producătorului.');
    }
    return updated;
}

/**
 * Creates a furnizor in the catalog and updates the local cache.
 * @param {{ code: string, name: string }} payload
 * @returns {Promise<{ id?: number|null, code: string, name: string, title?: string }>}
 */
export async function createFurnizor(payload) {
    const code = String(payload?.code ?? '').trim();
    const name = String(payload?.name ?? '').trim();
    if (!code || !name) {
        throw new Error('Codul și denumirea furnizorului sunt obligatorii.');
    }
    const response = await apiClient.post('/api/furnizori/', { code, name });
    const created = upsertFurnizor(response.data);
    if (!created) {
        throw new Error('Răspuns invalid la crearea furnizorului.');
    }
    return created;
}

/**
 * Updates a furnizor in the catalog and updates the local cache.
 * @param {{ id: number, code: string, name: string }} payload
 * @returns {Promise<{ id?: number|null, code: string, name: string, title?: string }>}
 */
export async function updateFurnizor(payload) {
    const id = payload?.id;
    const code = String(payload?.code ?? '').trim();
    const name = String(payload?.name ?? '').trim();
    if (id == null) {
        throw new Error('ID-ul furnizorului este obligatoriu.');
    }
    if (!code || !name) {
        throw new Error('Codul și denumirea furnizorului sunt obligatorii.');
    }
    const response = await apiClient.patch(`/api/furnizori/${id}/`, { code, name });
    const updated = upsertFurnizor(response.data);
    if (!updated) {
        throw new Error('Răspuns invalid la actualizarea furnizorului.');
    }
    return updated;
}

/**
 * Forces a fresh fetch of manufacturer + structure lookups from the API.
 * @returns {Promise<void>}
 */
export async function reloadProductLookups() {
    lookupsResolved = false;
    loadPromise = null;
    return ensureProductLookupsLoaded();
}

/**
 * @param {{ systems: string[], code: string, name: string }} payload
 * @returns {Promise<{ code: string, name: string, systems: string[] }>}
 */
export async function createProductChapter({ systems, code, name }) {
    const response = await apiClient.post('/api/product-structure/chapters/', {
        systems,
        code,
        name,
    });
    await reloadProductLookups();
    return response.data;
}

/**
 * @param {{ originalCode: string, systems: string[], code: string, name: string }} payload
 * @returns {Promise<{ code: string, name: string, systems: string[] }>}
 */
export async function updateProductChapter({ originalCode, systems, code, name }) {
    const encodedCode = encodeURIComponent(String(originalCode || '').trim());
    const response = await apiClient.patch(`/api/product-structure/chapters/${encodedCode}/`, {
        systems,
        code,
        name,
    });
    await reloadProductLookups();
    return response.data;
}

/**
 * @param {{ system: string, chapterCode: string, code: string, name: string }} payload
 * @returns {Promise<{ code: string, name: string }>}
 */
export async function createProductSubchapter({ system, chapterCode, code, name }) {
    const response = await apiClient.post('/api/product-structure/subchapters/', {
        system,
        chapter_code: chapterCode,
        code,
        name,
    });
    await reloadProductLookups();
    return response.data;
}

/**
 * @param {{ chapterCode: string, originalCode: string, code: string, name: string }} payload
 * @returns {Promise<{ code: string, name: string }>}
 */
export async function updateProductSubchapter({ chapterCode, originalCode, code, name }) {
    const encodedChapter = encodeURIComponent(String(chapterCode || '').trim());
    const encodedCode = encodeURIComponent(String(originalCode || '').trim());
    const response = await apiClient.patch(
        `/api/product-structure/chapters/${encodedChapter}/subchapters/${encodedCode}/`,
        { code, name },
    );
    await reloadProductLookups();
    return response.data;
}

/**
 * @param {string} system
 * @returns {Promise<Array<{ title: string, value: string }>>}
 */
export async function fetchSubcategoriesForSystem(system) {
    const normalizedSystem = String(system || '').trim().toUpperCase();
    if (!normalizedSystem) {
        return [];
    }
    const cache = fetchSubcategoriesForSystem.cache || (fetchSubcategoriesForSystem.cache = new Map());
    if (cache.has(normalizedSystem)) {
        return cache.get(normalizedSystem);
    }

    try {
        const response = await apiClient.get(`/api/subcategories/${normalizedSystem}/`);
        const options = (response.data?.categoryOptions || [])
            .map((option) => ({
                title: option.title || option.name,
                value: option.value || option.name,
            }))
            .filter((option) => option.title && option.value);
        cache.set(normalizedSystem, options);
        return options;
    } catch (error) {
        console.error(`Failed to load subcategories for ${normalizedSystem}.`, error);
        cache.set(normalizedSystem, []);
        return [];
    }
}

/**
 * Next progressive number for a category + subcategory, from the whole catalog.
 * Independent of price list, system, and manufacturer.
 * @param {string} chapterCode
 * @param {string} subchapterCode
 * @param {string} [excludeProductCode] code of the product being edited, so it does not consume the next number
 * @returns {Promise<string>}
 */
export async function fetchNextProgressiveNumber(chapterCode, subchapterCode, excludeProductCode = '') {
    const chapter = String(chapterCode || '').trim();
    const subchapter = String(subchapterCode || '').trim();
    if (!chapter || !subchapter) {
        return '';
    }
    const response = await apiClient.get('/api/product-structure/next-progressive/', {
        params: {
            chapter,
            subchapter,
            exclude: String(excludeProductCode || '').trim(),
        },
    });
    return String(response.data?.progressiveNumber || '').trim();
}

export { getManufacturerCodes, getFurnizori };
